import { type FC, type PropsWithChildren, createContext, useContext, useRef, useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useTranslations } from 'next-intl';

export interface Command {
  apply: () => any;
  undo: () => void;
  redo: () => void;
  update?: (params: any) => void;
  message: string;
}

export interface CommandContextValue {
  apply: (command: Command) => any;
  insert: (command: Command) => void;
  undo: () => void;
  redo: () => void;
  history: Array<Command>;
  pointer: number;
  setPointer: (value: number) => void;
}

export const CommandContext = createContext<CommandContextValue>({
  apply: () => {},
  insert: () => {},
  undo: () => {},
  redo: () => {},
  history: [],
  pointer: 0,
  setPointer: () => {},
});

export const CommandContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [history] = useState<Array<Command>>([]);

  // The pointer is held in a ref and mirrored into state. `undo` is captured by a toast
  // action rendered before later commands ran, so reading the state variable out of that
  // closure would read a stale pointer and undo the wrong command. The state copy exists
  // only so consumers re-render; every read below goes through the ref.
  const pointerRef = useRef<number>(0);
  const [pointer, setPointerState] = useState<number>(0);

  const setPointer = (value: number) => {
    pointerRef.current = value;
    setPointerState(value);
  };

  const { toast } = useToast();

  const tCommon = useTranslations('common');

  // Insert command at pointer location and remove everything after
  // this is because once you do a new action, you can't "redo" any
  // undone command anymore.
  const record = (command: Command) => {
    history.splice(pointerRef.current, Infinity, command);
    setPointer(pointerRef.current + 1);
  };

  const apply = (command: Command): any => {
    // Run the command
    const result = command.apply();

    // A command that failed changed nothing, so there is nothing to undo. Recorded anyway,
    // the history describes an action that never happened and its inverse later runs
    // against untouched state - a restore that lost a 409 race would be "undone" by
    // trashing nodes another user had just put back.
    if (typeof result?.then === 'function') {
      // The rejection is the command's own to report; this only decides whether the
      // action is remembered. Swallowed here so it is not raised twice.
      void result.then(() => record(command)).catch(() => undefined);
      return result;
    }

    record(command);

    return result;
  };

  const insert = (command: Command): void => {
    // Insert a command into history that has already been applied
    record(command);
  };

  const undo = () => {
    if (pointerRef.current === 0) {
      return;
    }

    const newPointer = pointerRef.current - 1;
    const cmdToUndo = history[newPointer];
    setPointer(newPointer);

    if (cmdToUndo) {
      cmdToUndo.undo();

      if (cmdToUndo.message) {
        toast({
          title: tCommon('undo'),
          description: `Reverted: ${cmdToUndo.message}`,
        });
      }
    }
  };

  const redo = () => {
    if (pointerRef.current >= history.length) {
      return;
    }

    const cmdToRedo = history[pointerRef.current];
    setPointer(pointerRef.current + 1);

    if (cmdToRedo) {
      cmdToRedo.redo();

      if (cmdToRedo.message) {
        toast({
          title: tCommon('redo'),
          description: `Re‑applied: ${cmdToRedo.message}`,
        });
      }
    }
  };

  return (
    <CommandContext.Provider
      value={{
        apply,
        insert,
        undo,
        redo,
        history,
        pointer,
        setPointer,
      }}
    >
      {children}
    </CommandContext.Provider>
  );
};

export function useCommandContext() {
  return useContext(CommandContext);
}
